// Phase 0092 — HR Executive Dashboard API client.
import apiClient from '@/shared/api/client'

export interface ExecutiveKPIs {
  total_active_employees: number
  present_today: number
  absent_today: number
  attendance_pct: number
  late_early_count: number
  critical_post_vacancies: number
  manpower_shortage_total: number
  uncertified_critical_deployments: number
  open_escalations: number
  overdue_tasks: number
  certifications_expiring_30d: number
  open_grievances: number
  open_capa_from_studies: number
  avg_daily_performance_score: number
  top_department: { department_name: string; avg_score: number } | null
  bottom_department: { department_name: string; avg_score: number } | null
  automation_health_status: string
}

export interface ExecutiveSummary {
  as_of_date: string
  as_of: string
  kpis: ExecutiveKPIs
}

export interface DepartmentHealth {
  department_id: string
  department_code: string
  department_name: string
  sanctioned_strength: number
  active_manpower: number
  present_today: number
  absent_today: number
  attendance_pct: number
  manpower_shortage: number
  critical_role_vacancy: number
  certified_count: number
  certified_pct: number
  open_tasks: number
  overdue_tasks: number
  escalations_open: number
  capa_open: number
  avg_performance_score: number
  status: 'GREEN' | 'AMBER' | 'RED'
}

export interface ManpowerAttendance {
  total_active_employees: number
  present_today: number
  absent_today: number
  half_day_today: number
  attendance_pct: number
  missing_punch_count: number
  manual_review_required_count: number
  sanctioned_strength_total: number
  manpower_shortage: number
  as_of_date: string
}

export interface SkillCertification {
  certified_total: number
  expiring_30d: number
  expired: number
  multi_skilled_count: number
  top_multi_skilled: Array<{
    employee_id: string
    emp_code: string
    emp_name: string
    skill_count: number
  }>
}

export interface TaskEscalation {
  as_of_date: string
  generated_today: number
  completed_today: number
  verified_today: number
  completion_pct_today: number
  overdue_total: number
  escalated_total: number
  escalation_by_level: { 1: number; 2: number; 3: number }
  open_escalations: number
  top_overdue_tasks: Array<{
    id: string
    title: string
    due_at: string | null
    assigned_to_user_id: string | null
    escalation_level: number
  }>
}

export interface StudyCAPA {
  as_of_date: string
  completed_today: number
  pending_verification: number
  below_standard: number
  open_capa: number
  overdue_capa: number
  training_recommendations_open: number
}

export interface PerformanceAwards {
  as_of_date: string
  avg_daily_score: number
  avg_monthly_score: number
  top_performers: Array<{
    employee_id: string
    emp_code: string
    emp_name: string
    total_score: number
    rank_overall: number | null
  }>
  bottom_performers: Array<{
    employee_id: string
    emp_code: string
    emp_name: string
    total_score: number
  }>
  top_department: { department_id: string | null; avg_score: number | null } | null
  bottom_department: { department_id: string | null; avg_score: number | null } | null
  awards_this_month: Array<{
    award_type: string
    employee_id: string | null
    department_id: string | null
    score_value: number
    citation: string | null
    is_published: boolean
  }>
}

export interface AutomationHealth {
  status: 'GREEN' | 'AMBER' | 'RED' | 'UNKNOWN'
  scheduler_jobs_active?: number
  open_escalations?: number
  notifications_pending?: number
  attendance_derivation_pending?: number
  missing_punch_count_today?: number
  certifications_expiring_30d?: number
  certifications_expired?: number
  recent_failure_count?: number
}

export interface ExecutiveDashboardFull {
  summary: ExecutiveSummary
  departments: DepartmentHealth[]
  manpower_attendance: ManpowerAttendance
  skill_certification: SkillCertification
  tasks_escalations: TaskEscalation
  studies_capa: StudyCAPA
  performance_awards: PerformanceAwards
  automation_health: AutomationHealth
}

const P = '/hr/executive-dashboard'

export const executiveDashboardApi = {
  full: async (onDate?: string): Promise<ExecutiveDashboardFull> =>
    (await apiClient.get<ExecutiveDashboardFull>(`${P}/full`,
      { params: onDate ? { on_date: onDate } : {} })).data,
  summary: async () =>
    (await apiClient.get<ExecutiveSummary>(`${P}/summary`)).data,
  departments: async () =>
    (await apiClient.get<DepartmentHealth[]>(`${P}/departments`)).data,
}
